import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
import { btn, c } from '../../theme';
import { CC_VIEW_LABELS, previewCcPages } from './access';

// The four standalone Contact Center permissions (migration 053). Each saves
// the moment it is ticked, like the portal's other standalone permissions.
// Each save is its own literal api.patch call rather than one call with a
// computed path: the contract test (backend/tests/integration/contract.test.js)
// finds call sites by reading source, and a path it cannot read is a call it
// silently does not check.
const PERMISSIONS = [
  {
    path: 'cc-reveal-phone', column: 'can_reveal_cc_phone',
    save: (id, value) => api.patch(`/admin/users/${id}/cc-reveal-phone`, { can_reveal_cc_phone: value }),
    label: "Reveal a caller's full phone number",
    note: 'On Repeat Callers. Every reveal is logged individually.',
  },
  {
    path: 'cc-reveal-account', column: 'can_reveal_cc_account',
    save: (id, value) => api.patch(`/admin/users/${id}/cc-reveal-account`, { can_reveal_cc_account: value }),
    label: "Reveal a caller's customer account number",
    note: 'On Repeat Callers. Every reveal is logged individually.',
  },
  {
    path: 'cc-absence-reasons', column: 'can_view_cc_absence_reasons',
    save: (id, value) => api.patch(`/admin/users/${id}/cc-absence-reasons`, { can_view_cc_absence_reasons: value }),
    label: 'See why an agent is absent (FMLA, sick, …)',
    note: 'On Staffing. Without it the reason shows only as "absent".',
  },
  {
    path: 'contact-center-manage', column: 'can_manage_contact_center',
    save: (id, value) => api.patch(`/admin/users/${id}/contact-center-manage`, { can_manage_contact_center: value }),
    label: 'Manage Contact Center settings',
    note: 'Thresholds, forecasts, yard map, email recipients and TV displays. Does not open any page by itself.',
  },
];

function PermissionToggle({ user, permission, onUpdated }) {
  const on = !!user[permission.column];
  const mutation = useMutation({
    mutationFn: () => permission.save(user.id, !on),
    onSuccess: () => {
      toast.success(`${user.display_name}: ${on ? 'removed' : 'granted'} "${permission.label}".`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update the permission.'),
  });
  return (
    <>
      <label style={s.permLabel}>
        <input
          type="checkbox"
          id={`cc-${permission.path}-${user.id}`}
          checked={on}
          onChange={() => mutation.mutate()}
          disabled={mutation.isPending}
          style={{ marginRight: 6 }}
        />
        {permission.label}
      </label>
      <div style={s.permNote}>{permission.note}</div>
    </>
  );
}

/**
 * The Contact Center part of a user's editor in User Management: a role
 * (which pages), departments (whose data), and four standalone permissions.
 *
 * Role and departments are saved together, with their own button, because
 * neither means anything alone — a role with no department opens nothing —
 * and the preview shows the combination before it is saved.
 */
export default function ContactCenterAccess({ user, onUpdated }) {
  const { data: options, isLoading, error } = useQuery({
    queryKey: ['admin-cc-options'],
    queryFn: () => api.get('/admin/contact-center/options'),
  });

  const savedRole = user.cc_role || '';
  const savedDepts = [...(user.cc_departments || [])].sort();
  const [ccRole, setCcRole] = useState(savedRole);
  const [depts, setDepts] = useState(savedDepts);
  const [saving, setSaving] = useState(false);

  if (user.role === 'super_admin') {
    return (
      <div style={s.bypassNote}>
        Super admins see every Contact Center page, for every department, and can change its settings.
      </div>
    );
  }
  if (isLoading) return <div style={s.muted}>Loading Contact Center options…</div>;
  if (error) return <div style={s.error}>Could not load Contact Center roles: {error.message}</div>;

  const role = options.roles.find((r) => r.role_name === ccRole) || null;
  const chosenDepts = options.departments.filter((d) => depts.includes(d.id));
  const preview = previewCcPages(role, chosenDepts);
  const dirty = ccRole !== savedRole || depts.join(',') !== savedDepts.join(',');

  function toggleDept(id) {
    setDepts((prev) => (prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id].sort()));
  }

  async function save() {
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.id}/cc-role`, { cc_role: ccRole || null });
      await api.put(`/admin/users/${user.id}/cc-departments`, { departments: depts });
      toast.success(`${user.display_name}: Contact Center access saved.`);
      onUpdated();
    } catch (err) {
      toast.error(err.message || 'Failed to save Contact Center access.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div style={s.fieldRow}>
        <label htmlFor={`cc-role-${user.id}`} style={s.fieldLabel}>Role</label>
        <select
          id={`cc-role-${user.id}`}
          value={ccRole}
          onChange={(e) => setCcRole(e.target.value)}
          style={s.select}
        >
          <option value="">No access</option>
          {options.roles.map((r) => (
            <option key={r.role_name} value={r.role_name}>{r.label}</option>
          ))}
        </select>
      </div>

      <div style={s.fieldRow}>
        <span style={s.fieldLabel}>Departments</span>
        <div style={s.deptRow}>
          {options.departments.map((d) => (
            <label key={d.id} style={s.deptLabel}>
              <input
                type="checkbox"
                id={`cc-dept-${d.id}-${user.id}`}
                checked={depts.includes(d.id)}
                onChange={() => toggleDept(d.id)}
                style={{ marginRight: 6 }}
              />
              {d.label}
            </label>
          ))}
        </div>
      </div>

      <div style={s.preview} aria-live="polite">
        {!role && <span>No Contact Center access.</span>}
        {role && !chosenDepts.length && (
          <span style={s.warn}>A role opens nothing until at least one department is chosen.</span>
        )}
        {preview.map((p) => (
          <div key={p.id}>
            <strong>{p.label}:</strong>{' '}
            {p.views.length
              ? p.views.map((v) => CC_VIEW_LABELS[v]).join(', ')
              : <span style={s.warn}>no pages — this role has none of {p.label}&apos;s pages</span>}
          </div>
        ))}
      </div>

      <div style={s.saveRow}>
        <button type="button" style={s.saveBtn} onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save Contact Center access'}
        </button>
      </div>

      <div style={{ ...s.fieldLabel, marginTop: '14px' }}>Permissions</div>
      {PERMISSIONS.map((p) => (
        <PermissionToggle key={p.path} user={user} permission={p} onUpdated={onUpdated} />
      ))}
    </div>
  );
}

const s = {
  fieldRow:   { display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' },
  fieldLabel: { fontSize: '11px', fontWeight: 600, color: c.textMuted, minWidth: '84px', paddingTop: '6px' },
  select: {
    padding: '6px 10px', fontSize: '12px', border: `1px solid ${c.borderStrong}`,
    borderRadius: '6px', fontFamily: 'inherit', color: c.textStrong, background: c.surface,
  },
  deptRow:    { display: 'flex', gap: '16px', flexWrap: 'wrap', paddingTop: '5px' },
  deptLabel:  { display: 'flex', alignItems: 'center', fontSize: '12px', color: c.textStrong, cursor: 'pointer' },
  preview: {
    fontSize: '12px', lineHeight: 1.6, color: c.textStrong, background: c.surface,
    border: `1px solid ${c.border}`, borderRadius: '8px', padding: '10px 12px',
  },
  warn:       { color: c.warningText },
  saveRow:    { display: 'flex', justifyContent: 'flex-end', marginTop: '8px' },
  saveBtn:    { ...btn.secondaryXs },
  permLabel:  { display: 'flex', alignItems: 'center', fontSize: '12px', color: c.textStrong, cursor: 'pointer', marginTop: '6px' },
  permNote:   { marginLeft: '20px', marginTop: '2px', fontSize: '11px', lineHeight: 1.4, color: c.textFaint, maxWidth: '380px' },
  bypassNote: {
    fontSize: '12px', color: c.textMuted, background: c.surface, border: `1px solid ${c.border}`,
    borderRadius: '8px', padding: '10px 12px', lineHeight: 1.6,
  },
  muted:      { fontSize: '12px', color: c.textFaint },
  error:      { fontSize: '12px', color: c.dangerText },
};
